import { useState, ChangeEvent } from 'react'
import { Button } from '@creanizer/ui'
import { Label } from '@creanizer/ui'
import { Textarea } from '@creanizer/ui'
import { Slider } from '@creanizer/ui'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@creanizer/ui'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@creanizer/ui'
import { GenerateButton } from '@creanizer/ui'
import { Checkbox } from '@creanizer/ui'
import { RadioGroup, RadioGroupItem } from '@creanizer/ui'
import { Upload, Plus, Sparkles, FileText, Users } from 'lucide-react'
import { useCharacterStore } from '../../../../stores/characterStore'
import { usePlotStore } from '../../../../stores/plotStore'
import { useWorldBuilderStore } from '../../../../stores/worldbuilderStore'
import { useSocietyStore } from '../../../../stores/societyStore'
import type { RelationshipInput, RelationshipCharacterInput } from '@creanizer/types'
import { RequiredRelationships } from './RequiredRelationships'
import { generateWorldContextText } from '@creanizer/utils'

interface RelationshipFormProps {
  input: RelationshipInput
  onChange: (input: RelationshipInput) => void
  onGenerate: () => void
  isGenerating: boolean
}

export function RelationshipForm({
  input,
  onChange,
  onGenerate,
  isGenerating
}: RelationshipFormProps) {
  const { characters } = useCharacterStore()
  const { savedPlots } = usePlotStore()
  const { savedWorlds: worlds } = useWorldBuilderStore()
  const { savedSocieties: societies } = useSocietyStore()
  const [inputMode, setInputMode] = useState<'saved' | 'text' | 'file'>('saved')
  const [selectedPlotId, setSelectedPlotId] = useState<string>('')
  const [selectedWorldId, setSelectedWorldId] = useState<string>('')
  const [selectedSocietyIds, setSelectedSocietyIds] = useState<string[]>([])
  const [mainCharacterId, setMainCharacterId] = useState<string>(
    input.mainCharacter?.id || ''
  )
  const [selectedCharacterIds, setSelectedCharacterIds] = useState<string[]>(
    input.savedCharacterIds?.filter(id => id !== input.mainCharacter?.id) || []
  )

  const handleFileUpload = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    const reader = new FileReader()
    reader.onload = (event) => {
      const text = event.target?.result as string
      onChange({
        ...input,
        characterFile: text,
        characterText: undefined,
        mainCharacter: undefined,
        secondCharacter: undefined,  // 二人目もクリア
        savedCharacterIds: undefined
      })
    }
    reader.readAsText(file)
  }

  const handleMainCharacterSelect = (characterId: string) => {
    if (!characterId || characterId === 'none') {
      setMainCharacterId('')
      onChange({
        ...input,
        mainCharacter: undefined,
        savedCharacterIds: selectedCharacterIds
      })
      return
    }

    const character = characters.find(c => c.id === characterId)
    if (!character) return

    setMainCharacterId(characterId)
    const charInput: RelationshipCharacterInput = {
      id: character.id,
      name: character.result.name,
      profile: character.result.summary,
      personality: typeof character.result.personality === 'string' 
        ? character.result.personality 
        : `${character.result.personality.traits?.join('、') || ''}`,
      background: typeof character.result.background === 'string'
        ? character.result.background
        : `${character.result.background.birthplace || ''} 出身。${character.result.background.occupation || ''}`
    }

    onChange({
      ...input,
      mainCharacter: charInput,
      savedCharacterIds: [characterId, ...selectedCharacterIds],
      characterText: undefined,
      characterFile: undefined
    })
  }

  const handleCharacterToggle = (characterId: string) => {
    const newSelectedIds = selectedCharacterIds.includes(characterId)
      ? selectedCharacterIds.filter(id => id !== characterId)
      : [...selectedCharacterIds, characterId]
    
    setSelectedCharacterIds(newSelectedIds)
    
    const allCharacterIds = mainCharacterId 
      ? [mainCharacterId, ...newSelectedIds]
      : newSelectedIds
    
    // 選択されたキャラクターの情報を収集
    const additionalCharacters = newSelectedIds.map(id => {
      const character = characters.find(c => c.id === id)
      if (!character) return null
      
      return {
        id: character.id,
        name: character.result.name,
        profile: character.result.summary,
        personality: typeof character.result.personality === 'string' 
          ? character.result.personality 
          : `${character.result.personality.traits?.join('、') || ''}`,
        background: typeof character.result.background === 'string'
          ? character.result.background
          : `${character.result.background.birthplace || ''} 出身。${character.result.background.occupation || ''}`
      }
    }).filter(Boolean) as RelationshipCharacterInput[]
    
    onChange({
      ...input,
      savedCharacterIds: allCharacterIds,
      secondCharacter: additionalCharacters[0] || undefined,  // 後方互換性のため
      additionalCharacters: additionalCharacters
    })
  }

  const handleWorldSelect = (worldId: string) => {
    if (!worldId || worldId === 'none') {
      setSelectedWorldId('')
      onChange({
        ...input,
        worldContext: undefined
      })
      return
    }

    setSelectedWorldId(worldId)
    const world = worlds.find(w => w.id === worldId)
    if (!world) return

    const context = generateWorldContextText(world.result)

    onChange({
      ...input,
      worldContext: context
    })
  }

  const handleSocietyToggle = (societyId: string) => {
    const newSelectedIds = selectedSocietyIds.includes(societyId)
      ? selectedSocietyIds.filter(id => id !== societyId)
      : [...selectedSocietyIds, societyId]
    
    setSelectedSocietyIds(newSelectedIds)
    
    // 選択された組織の情報を追加設定に含める
    if (newSelectedIds.length > 0) {
      const societyContexts = newSelectedIds.map(id => {
        const society = societies.find(s => s.id === id)
        if (!society) return null
        
        // 重要メンバーの情報
        const membersInfo = society.result.members?.importantMembers?.length > 0
          ? `\n重要メンバー:\n${society.result.members.importantMembers.map(m => 
              `  - ${m.name} (${m.role}): ${m.description}`
            ).join('\n')}`
          : ''
        
        // 組織構造の情報
        const structureInfo = society.result.structure?.hierarchy?.length > 0
          ? `\n階層構造:\n${society.result.structure.hierarchy.map(h => 
              `  - ${h.title} (${h.count}名): ${h.responsibilities}`
            ).join('\n')}`
          : ''
        
        // 関係性の情報
        const relationsInfo = society.result.features?.relations
          ? `\n他組織との関係: ${society.result.features.relations}`
          : ''
        
        // 組織文化の情報
        const cultureInfo = society.result.features?.culture
          ? `\n組織文化: ${society.result.features.culture}`
          : ''
        
        // 秘密・裏設定の情報（関係性生成に有用）
        const secretsInfo = society.result.features?.secrets
          ? `\n秘密・裏設定: ${society.result.features.secrets}`
          : ''
        
        return `【組織: ${society.result.basicInfo.name}】
種類: ${society.result.basicInfo.type}
規模: ${society.result.basicInfo.scale} (メンバー数: ${society.result.structure?.memberCount || '不明'})
目的: ${society.result.basicInfo.purpose || ''}
説明: ${society.result.basicInfo.description || ''}${membersInfo}${structureInfo}${relationsInfo}${cultureInfo}${secretsInfo}
${society.result.members?.notes ? `\n備考: ${society.result.members.notes}` : ''}`
      }).filter(Boolean).join('\n\n')
      
      // additionalSettingsに組織情報を追加（既存の内容は保持）
      const baseSettings = input.additionalSettings?.replace(/【選択された組織】[\s\S]*?(?=\n【|$)/, '') || ''
      onChange({
        ...input,
        additionalSettings: baseSettings + (societyContexts ? `\n\n【選択された組織】\n${societyContexts}` : '')
      })
    } else {
      // 組織が選択されていない場合は組織情報を削除
      const baseSettings = input.additionalSettings?.replace(/【選択された組織】[\s\S]*?(?=\n【|$)/, '') || ''
      onChange({
        ...input,
        additionalSettings: baseSettings.trim()
      })
    }
  }

  const handlePlotSelect = (plotId: string) => {
    setSelectedPlotId(plotId)
    const plot = savedPlots.find(p => p.id === plotId)
    if (!plot) return

    // プロット情報を追加設定に含める（キャラクタージェネレータと同じ形式）
    const plotContext = `
【プロット情報】
タイトル: ${plot.result.title}
テーマ: ${plot.result.themes.join('、')}

あらすじ:
${plot.result.synopsis}

主要キャラクター:
${plot.result.characters.map(c => `- ${c.name}: ${c.role} - ${c.description || ''}`).join('\n')}
    `.trim()
    
    // additionalSettingsのみを更新（characterTextは変更しない）
    onChange({
      ...input,
      additionalSettings: plotContext
    })
  }

  // タブ変更時に他のタブの入力をクリア
  const handleTabChange = (value: string) => {
    setInputMode(value as 'saved' | 'text' | 'file')
    
    // タブ切り替え時に他の入力をクリア
    if (value === 'saved') {
      onChange({
        ...input,
        characterText: undefined,
        characterFile: undefined
      })
    } else if (value === 'text') {
      setMainCharacterId('')
      setSelectedCharacterIds([])
      onChange({
        ...input,
        mainCharacter: undefined,
        secondCharacter: undefined,
        savedCharacterIds: undefined,
        characterFile: undefined
      })
    } else if (value === 'file') {
      setMainCharacterId('')
      setSelectedCharacterIds([])
      onChange({
        ...input,
        mainCharacter: undefined,
        secondCharacter: undefined,
        savedCharacterIds: undefined,
        characterText: undefined
      })
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <Label>キャラクター情報の入力方法</Label>
        <Tabs value={inputMode} onValueChange={handleTabChange} className="mt-2">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="saved">保存済み</TabsTrigger>
            <TabsTrigger value="text">直接入力</TabsTrigger>
            <TabsTrigger value="file">ファイル</TabsTrigger>
          </TabsList>

          <TabsContent value="saved" className="space-y-4">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Users className="h-4 w-4 text-primary" />
                <Label>メインキャラクター（必須）</Label>
              </div>
              <Select value={mainCharacterId || 'none'} onValueChange={handleMainCharacterSelect}>
                <SelectTrigger>
                  <SelectValue placeholder="中心となるキャラクターを選択してください" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">選択してください</SelectItem>
                  {characters.map((char) => (
                    <SelectItem key={char.id} value={char.id}>
                      {char.result.name} - {char.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {input.mainCharacter && (
                <div className="mt-2 p-3 bg-muted rounded-md text-sm">
                  <p className="font-medium">{input.mainCharacter.name}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {input.mainCharacter.profile}
                  </p>
                </div>
              )}
            </div>
            
            <div>
              <Label>関係性を生成するキャラクター（複数選択可）</Label>
              <p className="text-xs text-muted-foreground mb-2">
                メインキャラクターと関係性を持つキャラクターを選択してください
              </p>
              <div className="mt-2 max-h-48 overflow-y-auto border rounded-md p-2 space-y-2">
                {characters
                  .filter(char => char.id !== mainCharacterId)
                  .map((character) => (
                    <div key={character.id} className="flex items-start space-x-2">
                      <Checkbox
                        id={`char-${character.id}`}
                        checked={selectedCharacterIds.includes(character.id)}
                        onCheckedChange={() => handleCharacterToggle(character.id)}
                      />
                      <label
                        htmlFor={`char-${character.id}`}
                        className="text-sm cursor-pointer flex-1"
                      >
                        <span className="font-medium">{character.result.name}</span>
                        <span className="text-muted-foreground ml-2">
                          ({character.result.age}歳 / {character.result.role})
                        </span>
                        {character.result.summary && (
                          <p className="text-xs text-muted-foreground mt-1">
                            {character.result.summary.substring(0, 100)}...
                          </p>
                        )}
                      </label>
                    </div>
                  ))}
              </div>
              {selectedCharacterIds.length > 0 && (
                <p className="text-xs text-muted-foreground mt-1">
                  {selectedCharacterIds.length}名のキャラクターを選択中
                </p>
              )}
            </div>

            {selectedPlotId && (
              <div className="p-3 bg-blue-50 rounded-md">
                <p className="text-sm text-blue-700">
                  💡 プロットが選択されています。選択したキャラクターとプロットの登場人物の関係性が生成されます
                </p>
              </div>
            )}
          </TabsContent>

          <TabsContent value="text" className="space-y-4">
            <div>
              <Label>キャラクター情報を入力</Label>
              <Textarea
                className="mt-2"
                placeholder="キャラクターの名前、性格、背景などを自由に入力してください"
                value={input.characterText || ''}
                onChange={(e) => onChange({
                  ...input,
                  characterText: e.target.value,
                  mainCharacter: undefined,
                  secondCharacter: undefined,
                  savedCharacterIds: undefined,
                  characterFile: undefined
                })}
                rows={6}
              />
            </div>
          </TabsContent>

          <TabsContent value="file" className="space-y-4">
            <div>
              <Label>キャラクター設定ファイルをアップロード</Label>
              <input
                id="character-file-upload"
                type="file"
                accept=".txt"
                onChange={handleFileUpload}
                className="hidden"
              />
              <Button
                type="button"
                variant="outline"
                className="mt-2 w-full"
                onClick={() => document.getElementById('character-file-upload')?.click()}
              >
                <Upload className="h-4 w-4 mr-2" />
                ファイルを選択
              </Button>
              {input.characterFile && (
                <div className="mt-2 p-3 bg-muted rounded-md text-sm">
                  ファイルが読み込まれました
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* プロット選択セクション */}
      {savedPlots.length > 0 && (
        <div className="space-y-4 border-t pt-4">
          <div className="flex items-center gap-2 mb-2">
            <FileText className="h-4 w-4 text-primary" />
            <Label>保存済みプロットから選択（任意）</Label>
          </div>
          <Select value={selectedPlotId} onValueChange={handlePlotSelect}>
            <SelectTrigger>
              <SelectValue placeholder="プロットを選択すると、そのストーリーに適した関係性を生成します" />
            </SelectTrigger>
            <SelectContent>
              {savedPlots.map(plot => (
                <SelectItem key={plot.id} value={plot.id}>
                  {plot.title} ({plot.result.characters.length}人の登場人物)
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {selectedPlotId && (
            <div className="p-3 bg-muted rounded-md space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium">
                  選択中: {savedPlots.find(p => p.id === selectedPlotId)?.title}
                </p>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setSelectedPlotId('')
                    onChange({ ...input, additionalSettings: '' })
                  }}
                >
                  クリア
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                プロットの登場人物とストーリーに基づいて関係性が生成されます
              </p>
            </div>
          )}
        </div>
      )}

      <div className="space-y-4">
        <div>
          <div className="flex items-center justify-between mb-2">
            <Label>登場人数</Label>
            <span className="text-sm font-medium text-muted-foreground">
              合計 {input.numberOfCharacters + 1}人（メイン＋{input.numberOfCharacters}人）
            </span>
          </div>
          <Slider
            value={[input.numberOfCharacters]}
            onValueChange={([v]) => onChange({ ...input, numberOfCharacters: v })}
            min={1}
            max={15}
            step={1}
            className="mt-2"
          />
          <div className="flex justify-between mt-1 text-xs text-muted-foreground">
            <span>2人</span>
            <span>8人</span>
            <span>16人</span>
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <Label>関係性の複雑さ</Label>
            <span className="text-sm font-medium text-muted-foreground">
              レベル {input.complexityLevel || 5}
            </span>
          </div>
          <Slider
            value={[input.complexityLevel || 5]}
            onValueChange={([v]) => onChange({ ...input, complexityLevel: v })}
            min={1}
            max={10}
            step={1}
            className="mt-2"
          />
          <div className="flex justify-between mt-1 text-xs text-muted-foreground">
            <span>シンプル</span>
            <span>標準</span>
            <span>複雑</span>
          </div>
        </div>
      </div>

      <RequiredRelationships
        relationships={input.requiredRelationships || []}
        onChange={(relationships) => onChange({ ...input, requiredRelationships: relationships })}
      />

      {worlds.length > 0 && (
        <div>
          <Label>世界観の選択（オプション）</Label>
          <Select value={selectedWorldId || 'none'} onValueChange={handleWorldSelect}>
            <SelectTrigger>
              <SelectValue placeholder="世界観を選択すると、その世界の関係性として生成されます" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">選択しない</SelectItem>
              {worlds.map((world) => (
                <SelectItem key={world.id} value={world.id}>
                  {world.title}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {input.worldContext && (
            <div className="mt-2 p-2 bg-gray-50 rounded text-sm text-gray-600">
              世界観が選択されています
            </div>
          )}
        </div>
      )}

      {societies.length > 0 && (
        <div>
          <Label>組織を選択（複数可）</Label>
          <div className="mt-2 max-h-48 overflow-y-auto border rounded-md p-2 space-y-2">
            {societies.filter(society => society?.result?.basicInfo).map(society => (
              <div key={society.id} className="flex items-start space-x-2">
                <Checkbox
                  id={`society-${society.id}`}
                  checked={selectedSocietyIds.includes(society.id)}
                  onCheckedChange={() => handleSocietyToggle(society.id)}
                />
                <label
                  htmlFor={`society-${society.id}`}
                  className="text-sm cursor-pointer flex-1"
                >
                  <span className="font-medium">{society.result.basicInfo.name}</span>
                  <span className="text-muted-foreground ml-2">
                    ({society.result.basicInfo.type} / 規模: {society.result.basicInfo.scale})
                  </span>
                </label>
              </div>
            ))}
          </div>
          {selectedSocietyIds.length > 0 && (
            <p className="text-xs text-muted-foreground mt-1">
              {selectedSocietyIds.length}個の組織を選択中
            </p>
          )}
        </div>
      )}

      <div>
        <Label htmlFor="additionalSettings">追加設定（任意）</Label>
        <Textarea
          id="additionalSettings"
          className="mt-2"
          placeholder="関係性に含めたい特定の設定や条件を自由に記載してください。&#10;例：&#10;・過去に共通の事件に巻き込まれた経験がある&#10;・全員が同じ学校や組織に所属している&#10;・特定の秘密を共有している&#10;・三角関係を含める&#10;・世代を超えた関係を作る"
          value={input.additionalSettings || ''}
          onChange={(e) => onChange({ ...input, additionalSettings: e.target.value })}
          rows={4}
        />
        <p className="text-xs text-muted-foreground mt-1">
          ここに記載した内容は、生成される関係性に反映されます
        </p>
      </div>

      <div className="space-y-4 border-t pt-4">
        <div>
          <Label>生成モード</Label>
          <RadioGroup
            value={input.mode || 'detailed'}
            onValueChange={(value) => onChange({ ...input, mode: value as 'detailed' | 'image' })}
            className="mt-2 space-y-2"
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="detailed" id="detailed" />
              <Label htmlFor="detailed" className="font-normal cursor-pointer">
                詳細モード - 関係性の詳しい説明を生成
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="image" id="image" />
              <Label htmlFor="image" className="font-normal cursor-pointer">
                イメージモード - 最小限の情報で想像の余地を残す
              </Label>
            </div>
          </RadioGroup>
        </div>

        <div className="flex items-center space-x-2">
          <Checkbox
            id="proMode"
            checked={input.useProMode || false}
            onCheckedChange={(checked) => onChange({ ...input, useProMode: !!checked })}
          />
          <Label htmlFor="proMode" className="flex items-center gap-2 cursor-pointer">
            <Sparkles className="h-4 w-4 text-primary" />
            Proモード（高性能モデル使用）
          </Label>
        </div>

        <div className="flex items-center space-x-2">
          <Checkbox
            id="gemini3FlashMode"
            checked={input.useGemini3FlashMode || false}
            onCheckedChange={(checked) => onChange({ ...input, useGemini3FlashMode: !!checked })}
          />
          <Label htmlFor="gemini3FlashMode" className="cursor-pointer">
            Gemini 3 Flash モード（試験版）
          </Label>
        </div>
      </div>

      <GenerateButton
        onClick={onGenerate}
        isLoading={isGenerating}
        className="w-full"
      />
    </div>
  )
}